import {
  MULTI_PAYMENT_STATUSES,
  MULTI_PAYMENT_STEP_STATUSES,
  MULTI_PAYMENT_STEP_TYPES,
  type MultiPayment,
  type PaymentStep,
  type PaymentsCart,
} from '@chekin/core';
import {findDepositPayment, getHoldSummary} from './paymentKinds';

type TimelineRowKey = 'bank' | 'hold' | 'charge';

type TimelineRowState = 'wait' | 'on' | 'done' | 'fail' | 'void';

type TimelineRow = {
  key: TimelineRowKey;
  state: TimelineRowState;
  amount?: number;
  isAuthorisation?: boolean;
};

type PaymentFailureReason =
  | 'declined'
  | 'funds'
  | 'expired'
  | 'cvc'
  | 'authentication'
  | 'hold'
  | 'generic';

const HOLD_STEPS = [
  MULTI_PAYMENT_STEP_TYPES.securityDeposit,
  MULTI_PAYMENT_STEP_TYPES.retention,
];

const CHARGE_STEPS = [
  MULTI_PAYMENT_STEP_TYPES.damageProtection,
  MULTI_PAYMENT_STEP_TYPES.instantPayment,
];

const BANK_STEPS = [MULTI_PAYMENT_STEP_TYPES.paymentSetupConfirmation];

const SETTLED = [
  MULTI_PAYMENT_STEP_STATUSES.complete,
  MULTI_PAYMENT_STEP_STATUSES.skipped,
];

const DECLINE_REASONS: Record<string, PaymentFailureReason> = {
  insufficient_funds: 'funds',
  card_velocity_exceeded: 'funds',
  expired_card: 'expired',
  incorrect_cvc: 'cvc',
  invalid_cvc: 'cvc',
  authentication_required: 'authentication',
  payment_intent_authentication_failure: 'authentication',
  setup_intent_authentication_failure: 'authentication',
  card_declined: 'declined',
  generic_decline: 'declined',
  do_not_honor: 'declined',
};

function stepsOf(multiPayment: MultiPayment | undefined, types: string[]) {
  return (multiPayment?.steps ?? []).filter(step => types.includes(step.type));
}

function stateOfSteps(steps: PaymentStep[]): TimelineRowState {
  if (steps.length === 0) return 'wait';
  if (steps.some(step => step.status === MULTI_PAYMENT_STEP_STATUSES.error))
    return 'fail';
  if (steps.every(step => SETTLED.includes(step.status))) return 'done';
  return 'on';
}

function hasStepAfter(multiPayment: MultiPayment | undefined, types: string[]) {
  const indexes = stepsOf(multiPayment, types).map(step => step.index);
  if (indexes.length === 0) return false;
  const last = Math.max(...indexes);
  return (multiPayment?.steps ?? []).some(step => step.index > last);
}

type BuildTimelineArgs = {
  paymentsCart?: PaymentsCart;
  totalToPayNow: number;
  multiPayment?: MultiPayment;
  hasBankStep: boolean;
  isBankConfirming: boolean;
  isFailed: boolean;
};

function buildPaymentTimeline({
  paymentsCart,
  totalToPayNow,
  multiPayment,
  hasBankStep,
  isBankConfirming,
  isFailed,
}: BuildTimelineArgs): TimelineRow[] {
  const {heldTotal: holdAmount, isAuthorisation} = getHoldSummary(paymentsCart);
  const isComplete = multiPayment?.status === MULTI_PAYMENT_STATUSES.complete;
  const deposit = findDepositPayment(paymentsCart);

  const rows: TimelineRow[] = [];

  if (hasBankStep) {
    const bankSteps = stepsOf(multiPayment, BANK_STEPS);
    const bankState: TimelineRowState =
      isComplete || hasStepAfter(multiPayment, BANK_STEPS)
        ? 'done'
        : stateOfSteps(bankSteps) === 'fail'
        ? 'fail'
        : isBankConfirming || bankSteps.length > 0 || !isFailed
        ? 'on'
        : 'void';
    rows.push({key: 'bank', state: bankState});
  }

  if (holdAmount > 0 || deposit) {
    rows.push({
      key: 'hold',
      state: isComplete ? 'done' : stateOfSteps(stepsOf(multiPayment, HOLD_STEPS)),
      amount: holdAmount,
      isAuthorisation,
    });
  }

  if (totalToPayNow > 0) {
    rows.push({
      key: 'charge',
      state: isComplete ? 'done' : stateOfSteps(stepsOf(multiPayment, CHARGE_STEPS)),
      amount: totalToPayNow,
    });
  }

  if (!isFailed) return rows;

  return rows.map(row =>
    row.state === 'fail' || row.state === 'done' ? row : {...row, state: 'void'},
  );
}

function getFailureReason(
  multiPayment?: MultiPayment,
  stripeCode?: string,
): PaymentFailureReason {
  const failedStep = (multiPayment?.steps ?? []).find(
    step => step.status === MULTI_PAYMENT_STEP_STATUSES.error,
  );

  const code = stripeCode || failedStep?.error_code;
  const mapped = code ? DECLINE_REASONS[code] : undefined;
  if (mapped) return mapped;

  if (failedStep && HOLD_STEPS.includes(failedStep.type as MULTI_PAYMENT_STEP_TYPES)) {
    return 'hold';
  }

  return code ? 'declined' : 'generic';
}

export {buildPaymentTimeline, getFailureReason};
export type {PaymentFailureReason, TimelineRow, TimelineRowState};
